import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Servicios", href: "#servicios" },
  { label: "Diferenciadores", href: "#diferenciadores" },
  { label: "Casos de Éxito", href: "#casos" },
  { label: "Sobre Nosotros", href: "#nosotros" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    if (!isHome) return; // let normal navigation happen on other pages
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 max-w-[1440px] mx-auto flex items-center justify-between h-[72px]">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2 group"
          onClick={() => setOpen(false)}
        >
          <span className="w-2.5 h-2.5 bg-[#0047FF] block group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-black text-[15px] tracking-tight leading-none">
            TI Datos<span className="text-[#0047FF]">.</span>
          </span>
          <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3] ml-2 border-l border-[#E5E5E5] pl-3">
            WOK center
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={isHome ? l.href : `/${l.href}`}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={(e) => handleAnchor(e, l.href)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#171717] hover:text-[#0047FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={isHome ? "#contacto" : "/#contacto"}
            data-testid="header-cta"
            onClick={(e) => handleAnchor(e, "#contacto")}
            className="hidden md:inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-[#0047FF] transition-colors"
          >
            Contáctanos
            <span className="inline-block translate-y-[1px]">→</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#E5E5E5]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-[#E5E5E5] px-6 py-8 flex flex-col gap-5"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={isHome ? l.href : `/${l.href}`}
              onClick={(e) => handleAnchor(e, l.href)}
              data-testid={`mobile-nav-${l.href.replace("#", "")}`}
              className="font-display text-2xl font-bold text-[#171717] hover:text-[#0047FF]"
            >
              {l.label}
            </a>
          ))}
          <a
            href={isHome ? "#contacto" : "/#contacto"}
            onClick={(e) => handleAnchor(e, "#contacto")}
            data-testid="mobile-header-cta"
            className="mt-2 inline-flex items-center justify-center bg-[#0A0A0A] text-white px-5 py-4 font-mono text-[11px] uppercase tracking-[0.18em]"
          >
            Contáctanos →
          </a>
        </div>
      )}
    </header>
  );
}
